/* eslint-disable no-undef */
import React from "react";
import PropTypes from "prop-types";

import * as types from "../../constants/ComponentConstants";
import Section from "../common/Section";
import Form from "../common/Form";
import Fieldset from "../common/Fieldset";
import Row from "../common/Row";
import Button from "../common/Button";
import NumberInput from "../common/NumberInput";
import RadioButtonGroup from "../common/RadioButtonGroup";
import RadioButtonChild from "../common/RadioButtonWithChild";
import DownloadUploadFiles from "../common/DownloadUploadFiles";
import InfrastructurenInDeBuurtTable from "./InfrastructurenInDeBuurtTable";
import * as domainConstants from "../../constants/DomainConstants";
import Map from "../common/map/Map";
import Markers from "../common/map/Markers";
import { Circle } from 'react-leaflet';

const BovenLokaliteitPerInfrastructuurForm = (props) => {
	const {
		bovenLokaliteitPerInfrastructuur,
		infrastructuur,
		disabled,
		inEditMode,
		center,
		errors,
		onChange,
		onSave,
		soortenInfrastructuur,
		soortenInfrastructuurAll,
		onAddSportinfrastructuurInDeBuurt,
		onDelete,
		onEdit,
		newCoords,
		mapKey,
		topSportSjabloon,
		noFileComponents,
		fileSessionStarted,
		sjablonen,
		documenten,
		fileTypes,
		onDropFiles,
		onDeleteFile,
		maxMapFeaturesToLoad,
		osmBaseLayerUrl,
		validationMessageType
	} = props;

	const jaNee = [
		{ value: "true", label: 'Ja' },
		{ value: "false", label: 'Nee' }
	];

	// documentuploadHelpers
	function mapDropFiles(component) {
		return function (files) {
			onDropFiles(component, files);
		};
	}

	function mapDeleteFile(component) {
		return function (fileName) {
			onDeleteFile(component, fileName);
		};
	}

	function getFilesForSjabloonComponent(component) {
		if (sjablonen) {
			const folder = sjablonen.find(x => x.name === component);
			if (folder) return folder.files;
		}
		return [];
	}

	function getFilesForDossierComponent(component) {
		if (documenten) {
			const folder = documenten.find(x => x.name === component);
			if (folder) return folder.files;
		}
		return [];
	}

	const hasCoordinates = bovenLokaliteitPerInfrastructuur.breedtegraad && bovenLokaliteitPerInfrastructuur.lengtegraad;

	if (bovenLokaliteitPerInfrastructuur.sportinfrastructurenInDeBuurt == null) {
		bovenLokaliteitPerInfrastructuur.sportinfrastructurenInDeBuurt = [];
	}

	if (bovenLokaliteitPerInfrastructuur.sportinfrastructurenOpKaart == null) {
		bovenLokaliteitPerInfrastructuur.sportinfrastructurenOpKaart = [];
	}

	const markersToShow = [{
		lat: parseFloat(inEditMode ? newCoords.lat : bovenLokaliteitPerInfrastructuur.breedtegraad),
		long: parseFloat(inEditMode ? newCoords.lng : bovenLokaliteitPerInfrastructuur.lengtegraad),
		size: 'x-large',
		color: types.COLOR_ORANGE,
		tooltip: props.tooltip,
		isStar: true
	}];

	bovenLokaliteitPerInfrastructuur.sportinfrastructurenOpKaart.filter(i => i.spakkiid && i.tienMinutenWandelen).forEach((sportinfrastructuur, i) => {
		if (sportinfrastructuur.breedtegraad && sportinfrastructuur.lengtegraad)
			markersToShow.push({
				lat: parseFloat(sportinfrastructuur.breedtegraad),
				long: parseFloat(sportinfrastructuur.lengtegraad),
				tooltip: sportinfrastructuur.naam,
				size: 'x-large',
				color: types.COLOR_BLUE
			});
		else
			return "";
	});

	bovenLokaliteitPerInfrastructuur.sportinfrastructurenOpKaart.filter(i => !i.spakkiid && i.tienMinutenWandelen).forEach((sportinfrastructuur, i) => {
		if (sportinfrastructuur.breedtegraad && sportinfrastructuur.lengtegraad)
			markersToShow.push({
				lat: parseFloat(sportinfrastructuur.breedtegraad),
				long: parseFloat(sportinfrastructuur.lengtegraad),
				tooltip: sportinfrastructuur.naam,
				size: 'x-large',
				color: types.COLOR_TURQUOISE_DARK
			});
		else
			return "";
	});

	bovenLokaliteitPerInfrastructuur.sportinfrastructurenOpKaart.filter(i => !i.tienMinutenWandelen).forEach((sportinfrastructuur, i) => {
		if (sportinfrastructuur.breedtegraad && sportinfrastructuur.lengtegraad)
			markersToShow.push({
				lat: parseFloat(sportinfrastructuur.breedtegraad),
				long: parseFloat(sportinfrastructuur.lengtegraad),
				tooltip: sportinfrastructuur.naam,
				size: 'x-large',
				color: types.COLOR_YELLOW
			});
		else
			return "";
	});

	const circleCenter = [parseFloat(inEditMode ? newCoords.lat : bovenLokaliteitPerInfrastructuur.breedtegraad), parseFloat(inEditMode ? newCoords.lng : bovenLokaliteitPerInfrastructuur.lengtegraad)];
	const mijnMarkers = [<Markers key="theMarkers" markersToShow={markersToShow} />, <Circle key="theCircle" center={circleCenter} color="red" radius={600} fillOpacity={0} />];

	const TOPSPORTGELEGENHEID_TOOLTIP = "Dit betreft sportinfrastructuur waar er topsportwerking aanwezig is of zal zijn i.s.m. een topsportfederatie (in geval van nieuwbouw).";

	const TOPSPORTGELEGENHEID_TOOLTIP_WITH_SJABLOON = "In het definitieve aanvraagformulier moet het ingevulde sjabloon topsport kunnen worden toegevoegd om te kunnen scoren op dit criterium. Zie onderstaande link.";
	const AANTALBEDDEN_TOOLTIP = "Het minimaal aantal beschikbare bedden werd vastgelegd op 40. Indien jouw project dus over minstens 40 bedden in eigen beheer beschikt leidt dit tot een hogere mate van bovenlokaliteit.";

	const topsportprotocol = noFileComponents ? null : (<Row>
		<DownloadUploadFiles
			dossierId={bovenLokaliteitPerInfrastructuur.dossierId}
			title="Sjabloon"
			disabled={disabled}
			color={types.COLOR_PURPLE}
			size={6}
			tab={domainConstants.DocumentTab.BovenLokaliteit}
			component={domainConstants.ComponentName.TopsportProtocol}
			files={getFilesForSjabloonComponent(domainConstants.ComponentName.TopsportProtocol)}
			fileSessionStarted={fileSessionStarted} />

		<DownloadUploadFiles
			dossierId={bovenLokaliteitPerInfrastructuur.dossierId}
			infrastructuurId={bovenLokaliteitPerInfrastructuur.infrastructuurId}
			title="Topsport protocol"
			disabled={disabled}
			color={types.COLOR_PURPLE}
			size={6}
			tab={domainConstants.DocumentTab.BovenLokaliteit}
			component={domainConstants.ComponentName.TopsportProtocol}
			files={getFilesForDossierComponent(domainConstants.ComponentName.TopsportProtocol)}
			onDelete={mapDeleteFile(domainConstants.ComponentName.TopsportProtocol)}
			onDrop={mapDropFiles(domainConstants.ComponentName.TopsportProtocol)}
			fileTypes={fileTypes.TopsportProtocol}
			validationMessage={errors ? errors.ulTopsportProtocol : ''}
			validationMessageType={validationMessageType}
			fileSessionStarted={fileSessionStarted} />

	</Row>);

	let sectionTitle = props.sectionTitle ? props.sectionTitle : "Vul het antwoord op onderstaande vragen in";
	let formTitle = props.formTitle ? props.formTitle : 'Invulformulier bovenlokaliteit';

	const infrasInProject = bovenLokaliteitPerInfrastructuur.sportinfrastructurenInDeBuurt.filter(i => i.behoortTotMijnProject);
	const infrasBinnenCirkel = bovenLokaliteitPerInfrastructuur.sportinfrastructurenInDeBuurt.filter(i => i.behoortTotMijnProject === false && i.tienMinutenWandelen);
	const infrasBuitenCirkel = bovenLokaliteitPerInfrastructuur.sportinfrastructurenInDeBuurt.filter(i => i.behoortTotMijnProject === false && i.tienMinutenWandelen === false);

	const legendeCustomRows = [
		<Row key="legendeRow1"><i className="fa fa-star" style={{ color: "#ff8200", fontSize: "1.5em" }}/>&nbsp;&nbsp;{infrastructuur.display}</Row>,
		<Row key="legendeRow2"><i className="fa fa-map-marker" style={{ color: "#39b4e8", fontSize: "1.5em" }}/>&nbsp;&nbsp;Infrastructuren uit de sportdatabank</Row>,
		<Row key="legendeRow3"><i className="fa fa-map-marker" style={{ color: "#033e51", fontSize: "1.5em" }}/>&nbsp;&nbsp;Manueel toegevoegde infrastructuren</Row>,
		<Row key="legendeRow4"><i className="fa fa-map-marker" style={{ color: "#ffd100", fontSize: "1.5em" }}/>&nbsp;&nbsp;Infrastructuren op meer dan 600m</Row>
	];

	return (
		<Section title={sectionTitle} color={props.sectionTitleColor ? props.sectionTitleColor : types.COLOR_PURPLE}>
			<Form
				title={formTitle + ' "' + infrastructuur.display + '"'}
				name="bovenLokaliteit"
				rightButtonLabel="Bewaar"
				onRightButtonClick={onSave}
				rightButtonDisabled={disabled}>
				<Fieldset>
					<Row>
						<NumberInput
							label="Oppervlakte in m²"
							name="oppervlakte"
							onChange={onChange}
							value={bovenLokaliteitPerInfrastructuur.oppervlakte != null ? bovenLokaliteitPerInfrastructuur.oppervlakte.toString() : ""}
							size={4}
							disabled={disabled}
							validationMessage={errors ? errors.oppervlakte : ''}
							validationMessageType={validationMessageType}
							tooltip="Oppervlakte van de sportinfrastructuur. Voor openluchtsportvelden is dit de oppervlakte van het sportveld inclusief de uitloopzones en sportgerelateerde randinfrastructuur (dug-outs, kleedkamers, berging, tribune, ...), voor indoor infrastructuur is dit de geprojecteerde vloeroppervlakte van het gebouw of van de constructie. De oppervlakte ingenomen door de parking, wordt niet meegerekend."
							normalWhiteSpace
						/>
						<NumberInput
							label="Hoogte in m"
							name="hoogte"
							onChange={onChange}
							value={bovenLokaliteitPerInfrastructuur.hoogte != null ? bovenLokaliteitPerInfrastructuur.hoogte.toString() : ""}
							size={4}
							disabled={disabled}
							validationMessage={errors ? errors.hoogte : ''}
							validationMessageType={validationMessageType}
							tooltip="Hoogte van het gebouw of van de constructie. Ingrepen op de omgeving die nodig zijn om te voldoen aan vergunningsvoorwaarden zoals geluidsbermen bvb, dienen ook opgenomen te worden. Verlichtingspalen en afsluitingen worden niet meegerekend. Ook randinfrastructuur met beperkte hoogte zoals dug-outs, veiligheids- en vangnetten, e.d. worden niet meegerekend."
							normalWhiteSpace
						/>
						<NumberInput
							label="Volume in m³"
							name="volume"
							onChange={onChange}
							value={bovenLokaliteitPerInfrastructuur.volume != null ? bovenLokaliteitPerInfrastructuur.volume.toString() : ""}
							size={4}
							disabled={disabled}
							validationMessage={errors ? errors.volume : ''}
							validationMessageType={validationMessageType}
							tooltip="Volume van het gebouw of van de constructie boven het maaiveld. Ondergrondse ruimten worden niet meegerekend! Ingrepen op de omgeving die nodig zijn om te voldoen aan vergunningsvoorwaarden zoals geluidsbermen bvb, dienen ook opgenomen te worden."
							normalWhiteSpace
						/>
					</Row>
					<Row>
						{topSportSjabloon ? <div className="u-clear">
							<RadioButtonGroup
								label={"Topsportgelegenheid"}
								name="topsportGelegenheid"
								value={bovenLokaliteitPerInfrastructuur.topsportGelegenheid}
								onChange={onChange}
								disabled={disabled}
								items={jaNee}
								horizontal
								validationMessage={errors ? errors.topsportGelegenheid : ''}
								validationMessageType={validationMessageType}
								tooltip={TOPSPORTGELEGENHEID_TOOLTIP_WITH_SJABLOON}
								tooltipWindowWidth={35}
								tooltipWindowAlignRight={false}
								size={6}
							/>
							<div className="o-col-6">
								<a href={topSportSjabloon} className="externe-link minimal classic" target="_blank"><i className="fa fa-external-link"/> Toelichting topsportgebruik</a>
							</div>
						</div> :
							<RadioButtonChild
								label={"Topsportgelegenheid"}
								name="topsportGelegenheid"
								value={bovenLokaliteitPerInfrastructuur.topsportGelegenheid}
								onChange={onChange}
								disabled={disabled}
								items={jaNee}
								horizontal
								validationMessage={errors ? errors.topsportGelegenheid : ''}
								validationMessageType={validationMessageType}
								tooltip={TOPSPORTGELEGENHEID_TOOLTIP}
								tooltipWindowWidth={35}
								tooltipWindowAlignRight={false}
								childJa={topsportprotocol}
							/>}
						<NumberInput
							label="Aantal bedden"
							name="aantalBedden"
							optioneel
							onChange={onChange}
							value={bovenLokaliteitPerInfrastructuur.aantalBedden != null ? bovenLokaliteitPerInfrastructuur.aantalBedden.toString() : ""}
							size={3}
							disabled={disabled}
							noDecimals
							validationMessage={errors ? errors.aantalBedden : ''}
							validationMessageType={validationMessageType}
							tooltip={AANTALBEDDEN_TOOLTIP}
							normalWhiteSpace
						/>
					</Row>
				</Fieldset>
				<div>
					{hasCoordinates ?
						<div>
							<Fieldset title="Sportinfrastructuren in de buurt">
								<Fieldset>
									<Row>
										<Map
											mapName={mapKey}
											maxMapFeaturesToLoad={maxMapFeaturesToLoad}
											baseLayerUrl={osmBaseLayerUrl}
											baseLayerAttribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors, Tiles courtesy of <a href="https://geo6.be/">GEO-6</a>'
											children={mijnMarkers}
											zoom={15}
											legendeCustomRows={legendeCustomRows}
											showEmptyMap
											onlyBaseLayers
											customCenter={center}
										/>
									</Row>
									<Row>
										<p>&nbsp;</p>
										<p>
											De sportinfrastructuren die opgenomen zijn in onze sportdatabank en die zich bevinden in de buurt van deze infrastructuur "{infrastructuur.display}" zijn weergegeven op de kaart en in de onderstaande tabellen.
									<br /><br />
										</p>
										<p>&nbsp;</p>
									</Row>
								</Fieldset>
							</Fieldset>
							{infrasInProject.length > 0 ?
								<Fieldset title="Sportinfrastructuren waarvoor subsidies worden gevraagd">
									<Row>
										<InfrastructurenInDeBuurtTable
											infrastructurenInDeBuurt={infrasInProject}
											disabled
											soortenInfrastructuur={soortenInfrastructuur}
											soortenInfrastructuurAll={soortenInfrastructuurAll}
										/>
									</Row>
								</Fieldset>
								:
								null
							}
							<Fieldset title="Sportinfrastructuren in de buurt die zich bevinden binnen een straal van 600m van deze infrastructuur">
								<Row>
									{infrasBinnenCirkel.length > 0 ?
										<InfrastructurenInDeBuurtTable
											infrastructurenInDeBuurt={infrasBinnenCirkel}
											disabled={disabled}
											onDelete={onDelete}
											onEdit={onEdit}
											soortenInfrastructuur={soortenInfrastructuur}
											soortenInfrastructuurAll={soortenInfrastructuurAll}
										/>
										:
										null
									}
								</Row>
							</Fieldset>
							{infrasBuitenCirkel.length > 0 ?
								<Fieldset title="Sportinfrastructuren in de buurt die zich bevinden buiten een straal van 600m van deze infrastructuur">
									<Row>
										<InfrastructurenInDeBuurtTable
											infrastructurenInDeBuurt={infrasBuitenCirkel}
											onDelete={onDelete}
											onEdit={onEdit}
											soortenInfrastructuur={soortenInfrastructuur}
											soortenInfrastructuurAll={soortenInfrastructuurAll}
										/>
									</Row>
								</Fieldset>
								:
								null
							}
							<Fieldset>
								<Row>
									<Button text="Voeg ontbrekende sportinfrastructuur toe" onClick={onAddSportinfrastructuurInDeBuurt} disabled={disabled} />
									<br /><br />
								</Row>
								<Row>
									<p>
										Volgende sportinfrastructuren kunnen eventueel toegevoegd worden aan de lijst:
									<br /><br />

										<ul>
											<li>
												Bestaande sportinfrastructuur die niet opgenomen is in de sportdatabank.
										</li>
											<li>
												Nog niet gerealiseerde sportinfrastructuur die in een vorige subsidieronde werd gesubsidieerd.
										</li>
											<li>
												Nog niet gerealiseerde sportinfrastructuur waarvoor reeds een vergunning werd bekomen.
										</li>
										</ul>
										<br />
										Let op: de toegevoegde sportinfrastructuren moeten zich binnen een straal van 600m bevinden.
								</p>
								</Row>
							</Fieldset>
						</div>
						: ""}
				</div >
			</Form >
		</Section >
	);
};

// ----------------------------------------------------------------------------
BovenLokaliteitPerInfrastructuurForm.propTypes = {
	bovenLokaliteitPerInfrastructuur: PropTypes.object,
	infrastructuur: PropTypes.object.isRequired,
	disabled: PropTypes.bool,
	inEditMode: PropTypes.bool,
	center: PropTypes.object.isRequired,
	errors: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	soortenInfrastructuur: PropTypes.array,
	soortenInfrastructuurAll: PropTypes.array,
	tooltip: PropTypes.bool,
	onAddSportinfrastructuurInDeBuurt: PropTypes.func,
	onDelete: PropTypes.func,
	onEdit: PropTypes.func,
	newCoords: PropTypes.object,
	mapKey: PropTypes.string,
	topSportSjabloon: PropTypes.string,
	noFileComponents: PropTypes.bool,
	fileSessionStarted: PropTypes.bool,
	sjablonen: PropTypes.array.isRequired,
	documenten: PropTypes.array,
	fileTypes: PropTypes.object,
	onDropFiles: PropTypes.func,
	onDeleteFile: PropTypes.func,
	sectionTitle: PropTypes.string,
	sectionTitleColor: PropTypes.string,
	formTitle: PropTypes.string,
	maxMapFeaturesToLoad: PropTypes.string,
	osmBaseLayerUrl: PropTypes.string,
	validationMessageType: PropTypes.string
};

export default BovenLokaliteitPerInfrastructuurForm;
